import type {
   CompanyTotalsData,
   Breakdown,
   PeriodTotals,
} from "@laborchart-modules/lc-core-api/dist/api/totals/get-company-totals";

export type RangeOption = {
   days: number;
   translationKey: string;
};

export const RANGE_OPTIONS: RangeOption[] = [
   { days: 7, translationKey: "views.company.workforce_planning.totals_page.range_1_week" },
   { days: 14, translationKey: "views.company.workforce_planning.totals_page.range_2_weeks" },
   { days: 28, translationKey: "views.company.workforce_planning.totals_page.range_4_weeks" },
   { days: 56, translationKey: "views.company.workforce_planning.totals_page.range_8_weeks" },
   { days: 91, translationKey: "views.company.workforce_planning.totals_page.range_13_weeks" },
   { days: 182, translationKey: "views.company.workforce_planning.totals_page.range_6_months" },
   { days: 364, translationKey: "views.company.workforce_planning.totals_page.range_1_year" },
   { days: 730, translationKey: "views.company.workforce_planning.totals_page.range_2_years" },
   { days: 1095, translationKey: "views.company.workforce_planning.totals_page.range_3_years" },
   { days: 1825, translationKey: "views.company.workforce_planning.totals_page.range_5_years" },
];

export const DEFAULT_RANGE_DAYS = 91;

export function inferBreakdown(rangeDays: number): Breakdown {
   if (rangeDays <= 56) return "daily";
   if (rangeDays <= 182) return "weekly";
   if (rangeDays <= 364) return "monthly";
   if (rangeDays <= 1095) return "bimonthly";
   return "quarterly";
}

function mergeSeriesData(
   labor: PeriodTotals[],
   equipment: PeriodTotals[],
   toTimestamp: (period: string) => number,
): Array<[number, number]> {
   const map = new Map<string, number>();
   for (const item of labor) {
      map.set(item.period, (map.get(item.period) ?? 0) + item.resource_count);
   }
   for (const item of equipment) {
      map.set(item.period, (map.get(item.period) ?? 0) + item.resource_count);
   }
   return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, count]) => [toTimestamp(period), count]);
}

export function processCompanyTotalsForGraph(
   data: CompanyTotalsData,
   selectedView: "assignments" | "requests" | "both",
   I18n: { t: (key: string) => string },
): Highcharts.SeriesAreaOptions[] {
   const series: Highcharts.SeriesAreaOptions[] = [];
   const toTimestamp = (period: string) => new Date(period).getTime();

   if (selectedView === "assignments" || selectedView === "both") {
      series.push({
         name: I18n.t(`views.company.workforce_planning.totals_page.row_assigned`),
         type: "area",
         color: "#3498DB",
         data: mergeSeriesData(data.assignments.labor, data.assignments.equipment, toTimestamp),
      });
   }

   if (selectedView === "requests" || selectedView === "both") {
      series.push({
         name: I18n.t(`views.company.workforce_planning.totals_page.row_requested`),
         type: "area",
         color: "#F39C12",
         data: mergeSeriesData(data.requests.labor, data.requests.equipment, toTimestamp),
      });
   }

   series.push({
      name: I18n.t(`views.company.workforce_planning.totals_page.row_time_off_maintenance`),
      type: "area",
      color: "#9B59B6",
      data: mergeSeriesData(
         data.time_off_and_maintenance.labor,
         data.time_off_and_maintenance.equipment,
         toTimestamp,
      ),
   });

   return series.filter((s) => s.data && (s.data as Array<[number, number]>).length > 0);
}
